import { useEffect, useState } from "react";
import { Trash2, CheckCircle2, Clock3, LogOut } from "lucide-react";

const ADMIN_PASSWORD = "dslogistik2025";

interface Request {
  id: number;
  date: string;
  name: string;
  email: string;
  phone: string;
  services: string[];
  message: string;
  status: "Neu" | "In Bearbeitung" | "Erledigt";
}

export default function Admin() {
  const [auth, setAuth] = useState(false);
  const [pw, setPw] = useState("");
  const [requests, setRequests] = useState<Request[]>([]);
  const [error, setError] = useState("");

  useEffect(() => {
    if (typeof window !== "undefined" && sessionStorage.getItem("ds_admin") === "1") {
      setAuth(true);
    }
  }, []);

  useEffect(() => {
    if (auth && typeof window !== "undefined") {
      const data: Request[] = JSON.parse(localStorage.getItem("dslogistik_requests") || "[]");
      setRequests(data.sort((a, b) => b.id - a.id));
    }
  }, [auth]);

  const login = () => {
    if (pw === ADMIN_PASSWORD) {
      sessionStorage.setItem("ds_admin", "1");
      setAuth(true);
      setError("");
    } else {
      setError("Falsches Passwort");
    }
  };

  const updateStatus = (id: number, status: Request["status"]) => {
    const updated = requests.map((r) => (r.id === id ? { ...r, status } : r));
    setRequests(updated);
    localStorage.setItem("dslogistik_requests", JSON.stringify(updated));
  };

  const deleteRequest = (id: number) => {
    const updated = requests.filter((r) => r.id !== id);
    setRequests(updated);
    localStorage.setItem("dslogistik_requests", JSON.stringify(updated));
  };

  const logout = () => {
    sessionStorage.removeItem("ds_admin");
    setAuth(false);
  };

  if (!auth) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="w-full max-w-sm rounded-3xl border border-border/60 bg-card p-8 shadow-lg">
          <h1 className="mb-2 text-2xl font-bold text-foreground">DS Logistik Admin</h1>
          <p className="mb-6 text-sm text-muted-foreground">Kundenanfragen verwalten</p>
          <input
            type="password"
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && login()}
            placeholder="Passwort"
            className="mb-3 w-full rounded-xl border border-border/70 bg-background px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
          />
          {error && <p className="mb-3 text-xs text-red-500">{error}</p>}
          <button
            onClick={login}
            className="w-full rounded-full bg-primary py-3 text-sm font-bold text-white transition-all hover:-translate-y-px"
          >
            Anmelden
          </button>
        </div>
      </div>
    );
  }

  const newCount = requests.filter((r) => r.status === "Neu").length;
  const inProgressCount = requests.filter((r) => r.status === "In Bearbeitung").length;

  return (
    <div className="min-h-screen bg-background px-4 py-10 lg:px-8">
      <div className="mx-auto max-w-5xl">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Kundenanfragen</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              {requests.length} gesamt ·{" "}
              <span className="text-primary font-semibold">{newCount} neu</span>
              {inProgressCount > 0 && ` · ${inProgressCount} in Bearbeitung`}
            </p>
          </div>
          <button
            onClick={logout}
            className="flex items-center gap-2 rounded-full border border-border/60 px-4 py-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
          >
            <LogOut className="h-4 w-4" /> Abmelden
          </button>
        </div>

        {/* Stats */}
        <div className="mb-6 grid grid-cols-3 gap-4">
          {[
            { label: "Neue Anfragen", count: newCount, color: "text-primary bg-primary/10" },
            { label: "In Bearbeitung", count: inProgressCount, color: "text-yellow-600 bg-yellow-50" },
            { label: "Erledigt", count: requests.filter((r) => r.status === "Erledigt").length, color: "text-green-600 bg-green-50" },
          ].map(({ label, count, color }) => (
            <div key={label} className="rounded-2xl border border-border/60 bg-card p-4 text-center">
              <p className={`text-2xl font-bold font-mono ${color.split(" ")[0]}`}>{count}</p>
              <p className="mt-1 text-xs text-muted-foreground">{label}</p>
            </div>
          ))}
        </div>

        {/* Request list */}
        {requests.length === 0 ? (
          <div className="rounded-3xl border border-border/60 bg-card p-12 text-center">
            <p className="text-muted-foreground">Noch keine Anfragen vorhanden.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {requests.map((r) => (
              <div
                key={r.id}
                className={`rounded-3xl border bg-card p-6 transition-all ${
                  r.status === "Neu" ? "border-primary/30" : "border-border/60"
                }`}
              >
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-3">
                      <h3 className="text-lg font-semibold text-foreground">{r.name}</h3>
                      <span
                        className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                          r.status === "Neu"
                            ? "bg-primary/15 text-primary"
                            : r.status === "In Bearbeitung"
                            ? "bg-yellow-100 text-yellow-700"
                            : "bg-green-100 text-green-700"
                        }`}
                      >
                        {r.status}
                      </span>
                    </div>
                    <p className="mt-1 text-sm text-muted-foreground">
                      <a href={`mailto:${r.email}`} className="hover:text-primary">{r.email}</a>
                      {r.phone ? ` · ${r.phone}` : ""}
                      {" · "}
                      {new Date(r.date).toLocaleDateString("de-CH", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => updateStatus(r.id, "In Bearbeitung")}
                      title="In Bearbeitung setzen"
                      className="rounded-full border border-yellow-200 bg-yellow-50 p-1.5 text-yellow-600 hover:bg-yellow-100 transition-colors"
                    >
                      <Clock3 className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => updateStatus(r.id, "Erledigt")}
                      title="Als erledigt markieren"
                      className="rounded-full border border-green-200 bg-green-50 p-1.5 text-green-600 hover:bg-green-100 transition-colors"
                    >
                      <CheckCircle2 className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => deleteRequest(r.id)}
                      title="Anfrage löschen"
                      className="rounded-full border border-red-200 bg-red-50 p-1.5 text-red-500 hover:bg-red-100 transition-colors"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                {r.services && r.services.length > 0 && (
                  <div className="mt-4 flex flex-wrap gap-2">
                    {r.services.map((s) => (
                      <span key={s} className="rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
                        {s}
                      </span>
                    ))}
                  </div>
                )}

                {r.message && (
                  <p className="mt-3 rounded-xl bg-muted/40 px-4 py-2.5 text-sm text-muted-foreground">
                    {r.message}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
